<script lang="ts">
  import { _, locale } from 'svelte-i18n';
  import BlurredText from './Obfuscated.svelte';
  import { dataInClear } from '$lib/stores/auth';
  import { createEventDispatcher } from 'svelte';
  import { fly } from 'svelte/transition';
  import { findSimilarCompanies } from '$lib/api/similarity';
  import { addSimilarCompanies, mixedResults } from '$lib/stores/mixedResults';
  import Loading from './Loading.svelte';
  import type { CompanyWithContacts, Contact, FilterResponse } from '$lib/types/api';

  export let sourceName = '';
  export let sourceId = '';
  export let isOpen = false;

  const dispatch = createEventDispatcher();

  let similarCompanies: CompanyWithContacts[] = [];
  /** Every result before the client-side filter is applied. */
  let allSimilarCompanies: CompanyWithContacts[] = [];
  let loading = false;
  let error: string | null = null;

  // Fetch similar companies when panel opens
  $: if (isOpen && sourceId) {
    fetchSimilarCompanies();
  }

  // Helper function to assemble companies with their contacts
  function assembleCompaniesWithContacts(response: FilterResponse): CompanyWithContacts[] {
    const { companies, contacts } = response;

    // Ensure contacts is always an array (handle null, undefined, or missing)
    const contactsArray = contacts || [];

    // Group contacts by company ID
    const contactsByCompany = contactsArray.reduce<Record<string, Contact[]>>((acc, contact) => {
      if (!acc[contact.company_id]) {
        acc[contact.company_id] = [];
      }
      acc[contact.company_id].push(contact);
      return acc;
    }, {});

    // Attach contacts to companies
    const result = companies.map((company) => ({
      ...company,
      contacts: contactsByCompany[company.id] || []
    }));

    return result;
  }

  async function fetchSimilarCompanies() {
    loading = true;
    error = null;
    addedCompanyIds = new Set(); // Reset added companies when fetching new results

    try {
      const response = await findSimilarCompanies(sourceId);

      // Assemble companies with their contacts
      const companiesWithContacts = assembleCompaniesWithContacts(response);
      allSimilarCompanies = companiesWithContacts;

      // Filter by similarity score (>50%) first
      const highSimilarityCompanies = allSimilarCompanies.filter((company) => {
        const score = company.quality || 0;
        return score > 0.5;
      });

      // Then filter out companies that are already in the current results or have been added
      const existingIds = new Set($mixedResults.companies.map((c) => c.id));
      similarCompanies = highSimilarityCompanies.filter(
        (company) => !existingIds.has(company.id) && !addedCompanyIds.has(company.id)
      );
    } catch {
      error = $_('form.errors.similarCompaniesFailed');
      similarCompanies = [];
      allSimilarCompanies = [];
    } finally {
      loading = false;
    }
  }

  function addToResults(company: CompanyWithContacts) {
    // Check if already exists
    const existingIds = new Set($mixedResults.companies.map((c) => c.id));
    if (!existingIds.has(company.id)) {
      // Add to mixed results store
      addSimilarCompanies([company], sourceId, sourceName);
      dispatch('addToResults', company);
      // Mark as added and trigger re-filtering
      addedCompanyIds.add(company.id);
      addedCompanyIds = addedCompanyIds;
      // Re-filter to remove from list
      similarCompanies = similarCompanies.filter((c) => c.id !== company.id);
    }
  }

  function addMultiple(companies: CompanyWithContacts[]) {
    // Add multiple companies to mixed results
    addSimilarCompanies(companies, sourceId, sourceName);
    dispatch('addMultiple', companies);
    // Mark all as added and remove from list
    companies.forEach((c) => {
      addedCompanyIds.add(c.id);
    });
    addedCompanyIds = addedCompanyIds;
    // Remove added companies from the list
    const addedIds = new Set(companies.map((c) => c.id));
    similarCompanies = similarCompanies.filter((c) => !addedIds.has(c.id));
  }

  function addHighScoreCompanies(threshold = 0.85) {
    // Filter by score and remove already existing companies
    const existingIds = new Set($mixedResults.companies.map((c) => c.id));
    const filtered = filteredSimilarCompanies
      .filter((c) => (c.quality || 0) >= threshold)
      .filter((c) => !existingIds.has(c.id));

    if (filtered.length > 0) {
      addMultiple(filtered);
    }
  }

  function closeSimilar() {
    dispatch('close');
    // Clear data when closing
    similarCompanies = [];
    allSimilarCompanies = [];
    error = null;
  }

  // Track which companies have been added
  let addedCompanyIds = new Set();

  // Track which companies have expanded descriptions
  let expandedDescriptions = new Set();

  // Toggle description expansion
  function toggleDescription(companyId: string) {
    if (expandedDescriptions.has(companyId)) {
      expandedDescriptions.delete(companyId);
    } else {
      expandedDescriptions.add(companyId);
    }
    expandedDescriptions = expandedDescriptions; // Trigger reactivity
  }

  // Score filter slider
  let minScoreFilter = 50; // Start at 50% since we already filter <50%

  // Filter companies by score slider and remove already added ones
  $: filteredSimilarCompanies = similarCompanies.filter((company) => {
    const score = (company.quality || 0) * 100;
    return score >= minScoreFilter && !addedCompanyIds.has(company.id);
  });

  // Update the add functions to use filtered companies
  function addAllFilteredCompanies() {
    const existingIds = new Set($mixedResults.companies.map((c) => c.id));
    const newCompanies = filteredSimilarCompanies.filter((c) => !existingIds.has(c.id));
    if (newCompanies.length > 0) {
      addMultiple(newCompanies);
    }
  }
</script>

<div
  class="fixed right-0 top-0 z-50 flex h-screen w-96 flex-col border-l border-gray-200 bg-white shadow-xl"
  transition:fly={{ x: 384, duration: 300 }}
>
  <!-- Header -->
  <div class="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3">
    <div class="flex items-center justify-between">
      <div class="flex-1">
        <h3 class="text-sm font-semibold text-gray-900">{$_('headings.similarCompanies')}</h3>
        <p class="mt-0.5 text-xs text-gray-500">
          {$_('headings.basedOn')}: <BlurredText name={sourceName} masked={!$dataInClear} />
        </p>
        {#if !loading && allSimilarCompanies.length > 0}
          {@const highSimilarityCount = allSimilarCompanies.filter(
            (c) => (c.quality || 0) > 0.5
          ).length}
          {@const alreadyInSelectionCount = highSimilarityCount - similarCompanies.length}
          <p class="mt-1 text-xs text-gray-400">
            {#if filteredSimilarCompanies.length > 0}
              {$_('status.availableToAdd', { values: { count: filteredSimilarCompanies.length } })}
            {:else if addedCompanyIds.size > 0}
              {$_('status.allAdded', { values: { count: addedCompanyIds.size } })}
            {:else}
              {$_('status.available', { values: { count: similarCompanies.length } })}
            {/if}
            {#if alreadyInSelectionCount > 0}
              · {$_('status.alreadyInSelection', { values: { count: alreadyInSelectionCount } })}
            {/if}
          </p>
        {/if}
      </div>
      <button
        on:click={closeSimilar}
        class="text-gray-400 transition-colors hover:text-gray-600"
        style="background: none !important; box-shadow: none !important;"
      >
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  </div>

  <!-- Score Filter Slider -->
  {#if !loading && !error && similarCompanies.length > 0}
    <div class="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3">
      <div class="mb-2 flex items-center justify-between">
        <label class="text-xs font-medium text-gray-700">{$_('data.minimumScore')}</label>
        <span class="text-xs font-semibold text-accent-700">{minScoreFilter}%</span>
      </div>
      <input
        type="range"
        min="50"
        max="100"
        step="5"
        bind:value={minScoreFilter}
        class="slider h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
        style="--progress: {((minScoreFilter - 50) / 50) * 100}%"
      />
      <div class="mt-1 flex justify-between">
        <span class="text-xs text-gray-500">50%</span>
        <span class="text-xs text-gray-400">
          {$_('status.shown', {
            values: { visible: filteredSimilarCompanies.length, total: similarCompanies.length }
          })}
        </span>
        <span class="text-xs text-gray-500">100%</span>
      </div>
    </div>
  {/if}

  <!-- Results -->
  <div class="min-h-0 flex-1 overflow-y-auto">
    {#if loading}
      <div class="flex h-full items-center justify-center">
        <Loading />
      </div>
    {:else if error}
      <div class="p-4 text-center text-red-600">
        <p class="text-sm">{error}</p>
      </div>
    {:else if similarCompanies.length === 0}
      <div class="p-4 text-center text-gray-500">
        <p class="text-sm">{$_('status.noNewSimilarCompanies')}</p>
        {#if allSimilarCompanies.length > 0}
          <p class="mt-1 text-xs">
            {$_('status.companiesAlreadyInSelection', {
              values: {
                count: allSimilarCompanies.filter((c) => (c.quality || 0) > 0.5).length
              }
            })}
          </p>
        {:else}
          <p class="mt-1 text-xs">{$_('status.noCompaniesWithSimilarity')}</p>
        {/if}
      </div>
    {:else if filteredSimilarCompanies.length === 0}
      <div class="p-4 text-center text-gray-500">
        <p class="text-sm">
          {$_('status.noCompaniesMatchScore', { values: { score: minScoreFilter } })}
        </p>
        <p class="mt-1 text-xs">{$_('status.tryLoweringScore')}</p>
      </div>
    {:else}
      <div class="space-y-3 p-4">
        {#each filteredSimilarCompanies as company}
          <div
            class="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-3 transition-colors hover:bg-gray-100"
            on:click={() => toggleDescription(company.id)}
            on:mouseenter={() =>
              expandedDescriptions.add(company.id) && (expandedDescriptions = expandedDescriptions)}
            on:mouseleave={() =>
              expandedDescriptions.delete(company.id) &&
              (expandedDescriptions = expandedDescriptions)}
          >
            <!-- Score and Name -->
            <div class="mb-2 flex items-start justify-between">
              <div class="flex-1">
                <div class="mb-1 flex items-center gap-2">
                  <span
                    class="inline-flex items-center rounded-full bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-700"
                  >
                    {$_('status.match', {
                      values: { percent: Math.round((company.quality || 0) * 100) }
                    })}
                  </span>
                  {#if company.size}
                    <span class="text-xs text-gray-500">{company.size}</span>
                  {/if}
                </div>
                <h4 class="text-sm font-medium text-gray-900">
                  <BlurredText name={company.name} masked={!$dataInClear} />
                </h4>
              </div>
            </div>

            <!-- Description -->
            <p
              class="mb-2 text-xs text-gray-600 {expandedDescriptions.has(company.id)
                ? ''
                : 'line-clamp-2'}"
            >
              {$locale === 'de' && company.offerings_de
                ? company.offerings_de
                : company.offerings || company.purpose || ''}
            </p>

            <!-- Tags and Action -->
            <div class="flex items-center justify-between">
              <!--
                A website and a phone badge stood here, gated on
                `company.hasWebsite` and `company.hasPhone`. Both came from the
                demo rows this component used to carry; the API sends neither,
                so neither badge has ever rendered.

                `phone` does arrive and could drive the second one — that is a
                new badge on a live screen, so it is a decision rather than a
                cleanup. There is no website field at all: companyResponse in
                internal/httpapi/preview.go carries email, phone and the social
                links, and nothing else to hang it on.
              -->
              <div class="flex gap-1"></div>

              <button
                on:click={(e) => {
                  e.stopPropagation(); // Prevent triggering description expansion
                  addToResults(company);
                }}
                class="inline-flex items-center gap-1 rounded-md border border-accent-200 bg-white px-2 py-1 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-50"
                style="background: none !important; box-shadow: none !important;"
              >
                <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {$_('buttons.add')}
              </button>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <!-- Footer Actions -->
  <div class="flex-shrink-0 border-t border-gray-200 bg-white p-3">
    <div class="flex gap-2">
      <button
        on:click={addAllFilteredCompanies}
        class="flex-1 rounded-md bg-accent-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-accent-700"
        disabled={filteredSimilarCompanies.length === 0}
      >
        {$_('buttons.addAll')} ({filteredSimilarCompanies.length})
      </button>
      <button
        on:click={() => addHighScoreCompanies(0.85)}
        class="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
        style="background: none !important; box-shadow: none !important;"
        disabled={filteredSimilarCompanies.filter((c) => (c.quality || 0) >= 0.85).length === 0}
      >
        {$_('buttons.addHighScore')} ({filteredSimilarCompanies.filter(
          (c) => (c.quality || 0) >= 0.85
        ).length})
      </button>
    </div>
  </div>
</div>

<style>
  .line-clamp-2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  /* Custom slider styles */
  .slider::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    background: rgb(var(--accent-600));
    cursor: pointer;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .slider::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: rgb(var(--accent-600));
    cursor: pointer;
    border-radius: 50%;
    border: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .slider::-webkit-slider-runnable-track {
    background: linear-gradient(
      to right,
      rgb(var(--accent-600)) 0%,
      rgb(var(--accent-600)) var(--progress),
      rgb(var(--border-default)) var(--progress),
      rgb(var(--border-default)) 100%
    );
    border-radius: 4px;
  }
</style>
